<template>
    <RegularModal ref="modal" @closed="handleClosed">
        <template v-slot:title>
            <div class="is-flex is-align-items-center">
                <button
                    v-if="step === 'billing'"
                    type="button"
                    class="button is-ghost p-0 mr-3"
                    :aria-label="translate('event_status.modal.btn_back')"
                    @click="goBackToMain"
                >
                    <span class="icon"><i class="fas fa-arrow-left"></i></span>
                </button>
                <div>
                    <span>{{ modalTitle }}</span>
                </div>
            </div>
        </template>
        <template v-slot:body>
            <RequestForReviewForm
                v-if="step === 'billing'"
                ref="reviewForm"
                :initial-billing-details="store.metadata?.federationBillingDetails ?? null"
                :publish-on-approval="selectedVisibility === EVENT_VISIBILITY.Public"
            />

            <template v-else>
                <!-- Pending Review state: federation has requested approval and is awaiting an admin decision. -->
                <Notification v-if="showPendingReview" class="is-info">
                    <p class="has-text-weight-semibold mb-2">{{ translate("event_status.modal.pending_review_title") }}</p>
                    <p>{{ translate("event_status.modal.pending_review_message") }}</p>
                </Notification>

                <!-- Archived event warning -->
                <div v-else-if="!canChangeStatus" class="notification is-warning">
                    <p>{{ translate("event_status.modal.cannot_change_archived") }}</p>
                </div>

                <!-- Confirmation of a reversing action: replaces the main view so the consequence is stated before saving. -->
                <Notification v-else-if="step === 'confirmDraft' || step === 'confirmArchive'" :class="confirmNotificationClass">
                    <p class="has-text-weight-semibold mb-2"><i class="fas fa-exclamation-triangle"></i> {{ confirmTitle }}</p>
                    <p class="mb-3">{{ confirmMessage }}</p>
                    <a href="#" @click.prevent="goBackToMain">{{ translate("event_status.modal.keep_event_live") }}</a>
                </Notification>

                <template v-else>
                    <div v-if="!isDraft" class="box has-no-glow-effect py-3 px-4 mb-5">
                        <span class="tag is-success mr-2">{{ translate("event_status.modal.live") }}</span>
                        <i class="fas" :class="currentVisibilityOption?.icon"></i>
                        {{ currentVisibilityOption?.title }} &middot; {{ currentRegistrationModeOption.title }}
                    </div>

                    <!-- 1. Visibility: who can see this tournament. Always full cards, never collapsed. -->
                    <div class="mb-4">
                        <p class="section-heading mb-2">
                            {{ translate("event_status.modal.section_visibility") }}
                            <span v-if="isDraft && !visibilityDecided" class="has-text-warning">
                                {{ translate("event_status.modal.choose_one") }}
                            </span>
                        </p>

                        <div class="columns is-variable is-2 mb-0">
                            <div
                                v-for="option in visibilityOptions"
                                :key="option.value"
                                :id="option.value === EVENT_VISIBILITY.Private ? 'event-status-visibility-private' : undefined"
                                class="column is-6 is-flex pb-0"
                            >
                                <div class="radio-option" :class="{ 'is-selected': selectedVisibility === option.value }">
                                    <Radio name="visibility" :initial-value="option.value" v-model="selectedVisibility">
                                        <span class="radio-title"><i class="fas" :class="option.icon"></i> {{ option.title }}</span>
                                        <p class="radio-description">{{ option.description }}</p>
                                    </Radio>
                                </div>
                            </div>
                        </div>

                        <Notification v-if="showGoingPrivateWarning" class="is-warning">
                            {{ translate("event_status.modal.visibility_warning_going_private") }}
                        </Notification>

                        <Notification v-if="goingPublicWhileUnapproved" class="is-info">
                            {{ translate("event_status.modal.visibility_warning_going_public_unapproved") }}
                        </Notification>
                    </div>

                    <!-- 2. Registration mode. -->
                    <div class="mb-4">
                        <p class="section-heading mb-2">{{ translate("event_status.modal.section_registration") }}</p>

                        <div class="columns is-variable is-2 mb-0">
                            <div v-for="option in registrationModeOptions" :key="option.status" class="column is-6 is-flex pb-0">
                                <div class="radio-option" :class="{ 'is-selected': selectedStatus === option.status }">
                                    <Radio name="status" :initial-value="option.status" v-model="selectedStatus">
                                        <span class="radio-title"><i class="fas" :class="option.icon"></i> {{ option.title }}</span>
                                        <p class="radio-description">{{ option.description }}</p>
                                        <p v-if="option.showDates && registrationDatesText" class="radio-dates mt-2">
                                            <span class="has-text-grey-light" v-html="registrationDatesText"></span>
                                        </p>
                                    </Radio>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Other actions: reversing actions for live events. Rows with buttons, not cards, so they do not look like part of the form above. -->
                    <div v-if="!isDraft" class="status-actions pt-4">
                        <p class="section-heading mb-3">{{ translate("event_status.modal.section_status") }}</p>

                        <div v-for="option in lifecycleOptions" :key="option.step" class="is-flex is-align-items-center mb-3">
                            <button
                                type="button"
                                class="button is-outlined is-responsive-button action-button"
                                :class="option.buttonClass"
                                @click="step = option.step"
                            >
                                <span class="icon"><i class="fas" :class="option.icon"></i></span>
                                <span>{{ option.title }}</span>
                            </button>
                            <p class="radio-description ml-4">{{ option.description }}</p>
                        </div>
                    </div>
                </template>
            </template>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button v-if="!canChangeStatus || showPendingReview" class="button" @click="close()">
                    {{ translate("common.close") }}
                </button>
                <template v-else>
                    <button class="button" @click="step === 'billing' ? goBackToMain() : close()">
                        {{ step === "billing" ? translate("event_status.modal.btn_back") : translate("event_status.modal.btn_cancel") }}
                    </button>
                    <LoadingButton
                        :is-loading="isLoading"
                        class="button"
                        :class="confirmButtonClass"
                        @click="confirmStatusChange"
                        :disabled="!canConfirm"
                    >
                        <span>{{ confirmButtonText }}</span>
                    </LoadingButton>
                </template>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref, nextTick } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import Radio from "../../Common/Radio.vue";
    import { translate } from "../../Common/translations";
    import { useEditEventStore } from "../editEventStore";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import { convertToTimezone } from "../../Common/dateTimeUtils";
    import { EVENT_STATUS, EVENT_VISIBILITY, EventStatus, Visibility } from "../../Common/types";
    import Notification from "../../Common/Notification.vue";
    import { useAnalytics } from "../../Common/Analytics/useAnalytics";
    import HttpError from "../../Api/Utils/HttpError";
    import RequestForReviewForm from "./RequestForReviewForm.vue";
    import { useFeatureAnnouncement } from "../../Common/FeatureAnnouncements/useFeatureAnnouncement";

    interface StatusOption {
        status: EventStatus;
        icon: string;
        title: string;
        description: string;
        showDates: boolean;
    }

    type ModalStep = "main" | "billing" | "confirmDraft" | "confirmArchive";

    interface LifecycleOption {
        step: "confirmDraft" | "confirmArchive";
        icon: string;
        title: string;
        description: string;
        buttonClass: string;
    }

    const props = defineProps<{
        currentUserId?: number | null;
    }>();

    const store = useEditEventStore();
    const modal = ref<InstanceType<typeof RegularModal> | null>(null);
    const reviewForm = ref<InstanceType<typeof RequestForReviewForm> | null>(null);
    const selectedStatus = ref<EventStatus>(EVENT_STATUS.Draft);
    const selectedVisibility = ref<Visibility | undefined>(undefined);
    const step = ref<ModalStep>("main");
    const isLoading = ref<boolean>(false);
    const reviewFormWasShown = ref<boolean>(false);
    const { trackEvent, events } = useAnalytics();

    const currentStatus = computed(() => store.event.status);
    const currentVisibility = computed(() => store.event.visibility);
    const registrationStart = computed(() => store.event.registrationStart);
    const registrationEnd = computed(() => store.event.registrationEnd);
    const timezone = computed(() => store.event.timezone);
    const canChangeStatus = computed(() => store.metadata?.canChangeStatus ?? true);
    const isDraft = computed(() => currentStatus.value === EVENT_STATUS.Draft);
    const hasStatusChanged = computed(() => selectedStatus.value !== currentStatus.value);
    const hasVisibilityChanged = computed(() => (selectedVisibility.value ?? EVENT_VISIBILITY.None) !== currentVisibility.value);
    const isFreshFederation = computed(() => store.metadata?.federationApproved === false);
    // Review is a federation-level state, resolved by the backend from the federation's approval request.
    const showPendingReview = computed(() => store.metadata?.isUnderReview ?? false);
    const visibilityDecided = computed(() => selectedVisibility.value !== undefined);

    // An unapproved federation can never end up Public without review — this covers both newly
    // choosing Public and an event that is already Public (e.g. approval was revoked after the
    // fact) staying or changing state while Public. Reverting to Draft or archiving never goes
    // through this: those are separate confirmation steps that do not read the visibility choice.
    const goingPublicWhileUnapproved = computed(() => isFreshFederation.value && selectedVisibility.value === EVENT_VISIBILITY.Public);

    const showGoingPrivateWarning = computed(
        () =>
            currentVisibility.value === EVENT_VISIBILITY.Public &&
            selectedVisibility.value === EVENT_VISIBILITY.Private &&
            currentStatus.value !== EVENT_STATUS.Draft,
    );

    const visibilityOptions = computed(() => [
        {
            value: EVENT_VISIBILITY.Private,
            icon: "fa-link",
            title: translate("event_status.modal.visibility_private"),
            description: translate("event_status.modal.visibility_private_desc"),
        },
        {
            value: EVENT_VISIBILITY.Public,
            icon: "fa-globe",
            title: translate("event_status.modal.visibility_public"),
            description: translate("event_status.modal.visibility_public_desc"),
        },
    ]);

    const registrationModeOptions = computed((): StatusOption[] => [
        {
            status: EVENT_STATUS.RegistrationBySchedule,
            icon: "fa-calendar-alt",
            title: translate(isDraft.value ? "event_status.modal.by_schedule_recommended" : "event_status.modal.by_schedule"),
            description: translate("event_status.modal.by_schedule_desc"),
            showDates: true,
        },
        {
            status: EVENT_STATUS.RegistrationOpen,
            icon: "fa-infinity",
            title: translate("event_status.modal.always_open"),
            description: translate("event_status.modal.always_open_desc"),
            showDates: false,
        },
    ]);

    const lifecycleOptions = computed((): LifecycleOption[] => [
        {
            step: "confirmDraft",
            icon: "fa-pencil-alt",
            title: translate("event_status.modal.back_to_draft"),
            description: translate("event_status.modal.back_to_draft_desc"),
            buttonClass: "is-warning",
        },
        {
            step: "confirmArchive",
            icon: "fa-archive",
            title: translate("event_status.modal.archive_event"),
            description: translate("event_status.modal.archive_event_desc"),
            buttonClass: "is-danger",
        },
    ]);

    // Summary strip of what is saved right now (not the pending selection below it).
    const currentVisibilityOption = computed(() => visibilityOptions.value.find(option => option.value === currentVisibility.value));
    const currentRegistrationModeOption = computed(
        () => registrationModeOptions.value.find(option => option.status === currentStatus.value) ?? registrationModeOptions.value[0],
    );

    const isConfirmStep = computed(() => step.value === "confirmDraft" || step.value === "confirmArchive");
    const confirmTitle = computed(() =>
        translate(step.value === "confirmDraft" ? "event_status.modal.confirm_draft_title" : "event_status.modal.confirm_archive_title"),
    );
    const confirmMessage = computed(() =>
        translate(
            step.value === "confirmDraft" ? "event_status.modal.confirm_draft_message" : "event_status.modal.confirm_archive_message",
        ),
    );
    const confirmNotificationClass = computed(() => (step.value === "confirmDraft" ? "is-warning" : "is-danger"));

    const canConfirm = computed(() => {
        if (showPendingReview.value) {
            return false;
        }
        if (step.value === "billing" || isConfirmStep.value) {
            return true;
        }
        if (!visibilityDecided.value) {
            return false;
        }
        if (goingPublicWhileUnapproved.value) {
            return true;
        }
        return hasVisibilityChanged.value || hasStatusChanged.value;
    });

    const modalTitle = computed(() => {
        if (step.value === "billing") {
            return translate("event_status.modal.title.organization");
        }
        return isDraft.value ? translate("event_status.modal.title.publish") : translate("event_status.modal.title.manage");
    });

    const confirmButtonText = computed(() => {
        if (step.value === "confirmDraft") {
            return translate("event_status.modal.back_to_draft");
        }
        if (step.value === "confirmArchive") {
            return translate("event_status.modal.archive_event");
        }
        if (step.value === "billing") {
            return translate("event_status.modal.btn_submit_for_review");
        }
        if (goingPublicWhileUnapproved.value) {
            return translate("event_status.modal.btn_continue");
        }
        return translate("common.save");
    });

    const confirmButtonClass = computed(() => {
        if (step.value === "confirmDraft") {
            return "is-warning";
        }
        return step.value === "confirmArchive" ? "is-danger" : "is-primary";
    });

    const registrationDatesText = computed(() => {
        if (!registrationStart.value || !registrationEnd.value) {
            return "";
        }
        const opens = translate("event_status.modal.opens");
        const closes = translate("event_status.modal.closes");
        return `${opens}: ${formatDate(registrationStart.value)}<br />${closes}: ${formatDate(registrationEnd.value)}`;
    });

    function open(initialStep: ModalStep = "main") {
        selectedStatus.value = currentStatus.value === EVENT_STATUS.Draft ? EVENT_STATUS.RegistrationBySchedule : currentStatus.value;
        selectedVisibility.value = currentVisibility.value === EVENT_VISIBILITY.None ? undefined : currentVisibility.value;
        step.value = "main";
        reviewFormWasShown.value = false;

        // Jumping straight to billing (e.g. from the registration-cap banner) always means
        // "I want approval" — reuse the existing going-public-while-unapproved billing flow as-is.
        // Visibility itself is left untouched (a cap-triggered request keeps the event private;
        // see publishEventOnApproval below), so it stays whatever it already was seeded to above.
        if (initialStep === "billing") {
            step.value = "billing";
        }

        modal.value?.open();

        // Only for the main step - the visibility radios aren't rendered on the billing step, nor
        // for an archived event (warning shown instead) or a federation under review (pending-review
        // notice shown instead). Spotlight only after the organizer has opened this modal themselves
        // (not automatically on page load), and only once Vue has flushed the DOM update the open()
        // call above triggered, since the target radio isn't in the DOM until then.
        if (initialStep === "main" && canChangeStatus.value && !showPendingReview.value && props.currentUserId) {
            nextTick(() => {
                useFeatureAnnouncement().showEligible(props.currentUserId as number, [
                    { key: "event_status_new_private_visibility", element: "#event-status-visibility-private" },
                ]);
            });
        }
    }

    function close() {
        modal.value?.close();
    }

    function goBackToMain() {
        step.value = "main";
    }

    function handleClosed() {
        if (reviewFormWasShown.value && !isLoading.value) {
            trackEvent(events.Organizer.ReviewFormCanceled);
        }

        reviewFormWasShown.value = false;
        step.value = "main";
    }

    function formatDate(dateString: string): string {
        if (!dateString) {
            return "";
        }
        const localDate = convertToTimezone(dateString, timezone.value);
        return localDate;
    }

    function confirmStatusChange() {
        if (step.value === "main" && goingPublicWhileUnapproved.value) {
            step.value = "billing";
            return;
        }

        if (step.value === "billing") {
            submitForReview();
            return;
        }

        // Back to draft / archive save only the status; pending visibility and registration edits are dropped.
        const request = isConfirmStep.value
            ? {
                  status: step.value === "confirmDraft" ? EVENT_STATUS.Draft : EVENT_STATUS.Archived,
                  visibility: currentVisibility.value as Visibility,
              }
            : { status: selectedStatus.value, visibility: selectedVisibility.value as Visibility };
        // Read before the request: the store update flips isDraft by the time .then runs.
        const messageKey = isDraft.value && !isConfirmStep.value ? "event_status.success.published" : "event_status.success.updated";

        isLoading.value = true;

        return store
            .changeEventStatus(request)
            .then(() => {
                successToast(translate(messageKey));
                close();
            })
            .catch(handleError)
            .finally(() => {
                isLoading.value = false;
            });
    }

    function submitForReview() {
        if (!reviewForm.value) {
            return;
        }

        reviewForm.value.clearValidationErrors();
        const data = {
            ...reviewForm.value.getData(),
            // A cap-triggered request (jumped straight to billing, visibility left untouched at
            // Private) asks only to lift the cap - the event stays private. Explicitly choosing
            // Public in the main step is the only case that publishes on approval.
            publishEventOnApproval: selectedVisibility.value === EVENT_VISIBILITY.Public,
        };

        trackEvent(events.Organizer.PublishAttempted);
        reviewFormWasShown.value = true;

        isLoading.value = true;

        return store
            .submitForReview(data)
            .then(() => {
                trackEvent(events.Organizer.ReviewSubmitted);
                reviewFormWasShown.value = false;

                successToast(translate("event_status.success.submitted_for_review"));
                close();
            })
            .catch((error: HttpError) => {
                trackEvent(events.Organizer.ReviewSubmitFailed);

                if (error.validationErrors) {
                    const fieldErrors: Record<string, string[]> = {};
                    error.validationErrors.forEach(detail => {
                        fieldErrors[detail.field] = detail.message;
                    });
                    reviewForm.value?.setValidationErrors(fieldErrors);
                } else {
                    handleError(error);
                }
            })
            .finally(() => {
                isLoading.value = false;
            });
    }

    defineExpose({ open, close });
</script>

<style scoped>
    .radio-option {
        display: block;
        flex: 1;
        padding: 1rem;
        border: 2px solid var(--bulma-grey);
        border-radius: var(--bulma-radius);
        cursor: pointer;
        transition: all 0.2s;
        margin-bottom: 0.75rem;
        background-color: var(--bulma-card-background-color);
    }

    .radio-option:hover {
        border-color: var(--bulma-grey-light);
        background-color: var(--bulma-box-background-color);
    }

    .radio-option.is-selected {
        border-color: var(--bulma-primary);
        background-color: var(--bulma-box-background-color);
    }

    .fontawesome-radio:hover {
        background-color: unset;
    }

    .radio-option :deep(.fontawesome-radio) {
        display: flex;
        align-items: flex-start;
        width: 100%;
    }

    .radio-option :deep(.fontawesome-radio input[type="radio"]) {
        margin-right: 0.75rem;
        margin-top: 0.25rem;
    }

    .section-heading {
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: var(--bulma-grey-light);
    }

    .status-actions {
        border-top: 1px solid var(--bulma-grey);
    }

    /* em, so the shared width follows the button's font size and shrinks with is-responsive-button on phones. */
    .action-button {
        flex: 0 0 11em;
    }

    .radio-title {
        font-weight: 600;
        margin-bottom: 0.25rem;
        font-size: 1rem;
        color: var(--bulma-text);
    }

    .radio-description {
        color: var(--bulma-grey-light);
        font-size: 0.9rem;
        line-height: 1.4;
    }

    .radio-dates {
        font-size: 0.85rem;
        color: var(--bulma-grey-light);
    }
</style>
